import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  reset,
  getSignerDetailsAndSettings,
  createSigner,
  getIpAddress
} from '../../redux/actions/SignerAction';
import '../../App.css';
import Loading from '../Loader/Loader';
import { faDownload, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorPage from '../Error/ErrorPage';
import { useTranslation } from 'react-i18next';
import BankIDlogoImage from '../../assets/images/BankIDlogo.png';

const TYPE_URL = "914600001";
const TYPE_DOCUMENT = "914600000";

const SignerDetails = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('common');
  const {
    createSignerUrl,
    signerDetails,
    fetching,
    error,
  } = useSelector((state) => ({
    createSignerUrl: state.sign.createSignerUrl,
    signerDetails: state.sign.signerDetailsAndSettings,
    fetching: state.sign.fetching,
    error: state.error.isError
  }));

  const [hasTriggeredCreate, setHasTriggeredCreate] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [state, setState] = useState({
    signerName: "",
    termsAndConditions: [],
    signer: {},
    agreedTnC: false,
    isTncRead: false,
    genericError: false,
    AcceptedTimeStamp: new Date(),
    alreadySigned: false,
    isCancelled: false,
    languageCode: "",
    portallogo: "",
    isValidSequenceSigner: false,
    isValidateSigner: true,
    sequenceSignerMessage: ""
  });


  const convertUTCDateToLocalDate = (date) => {
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    ));
  };

  const getBrowserInfo = () => {
    const agent = navigator.userAgent.toLowerCase();
    if (agent.includes("edg/")) return "Edge (Chromium)";
    if (agent.includes("edge")) return "MS Edge (Legacy)";
    if (agent.includes("opr") || agent.includes("opera")) return "Opera";
    if (agent.includes("chrome") && !agent.includes("edg/")) return "Chrome";
    if (agent.includes("firefox")) return "Firefox";
    if (agent.includes("safari") && !agent.includes("chrome")) return "Safari";
    if (agent.includes("trident")) return "Internet Explorer";
    return "Other";
  };

  const getSystemInfo = () => {
    const agent = navigator.userAgent;
    if (agent.includes("Windows NT 10.0")) return "Windows 10/11";
    if (agent.includes("Android")) return "Android";
    if (agent.includes("iPhone")) return "iOS";
    if (agent.includes("Mac")) return "macOS";
    if (agent.includes("Linux")) return "Linux";
    return "Unknown";
  };

  useEffect(() => {
    localStorage.removeItem('pendingSigner');
    const query = new URLSearchParams(window.location.search);
    const Id = query.get('id');

    if (!Id) {
      setState(prev => ({ ...prev, genericError: true }));
      return;
    }

    dispatch(reset());
    dispatch(getSignerDetailsAndSettings(Id));
  }, [dispatch]);

  useEffect(() => {
    if (fetching) return;
    if (hasTriggeredCreate) return;
    const data = signerDetails?.data;
    if (!data || !data.SignerId) return;
    const bypassTerms = window.globalConfig.UseTermsAndConditionPage !== "true";

    const noTerms =
      bypassTerms ||
      !data.TermsAndConditions ||
      data.TermsAndConditions.length === 0;
    const shouldAutoProceed =
      !data.IsSigned &&
      !data.IsCancelled &&
      data.IsValidSequenceSigner &&
      !data.IsValidateSigner &&
      noTerms;
    if (shouldAutoProceed) {
      setHasTriggeredCreate(true);
      dispatch(createSigner(data));
    }
  }, [signerDetails, fetching, hasTriggeredCreate, dispatch]);

  useEffect(() => {
    if (fetching) return;
    if (hasRedirected) return;
    const data = signerDetails?.data;
    if (!data || !data.SignerId) return;
    const bypassTerms = window.globalConfig.UseTermsAndConditionPage !== "true";

    const noTerms =
      bypassTerms ||
      !data.TermsAndConditions ||
      data.TermsAndConditions.length === 0;
    if (
      !data.IsSigned &&
      !data.IsCancelled &&
      data.IsValidSequenceSigner &&
      data.IsValidateSigner &&
      noTerms &&
      data.IDVerificationURL
    ) {
      setHasRedirected(true);
      localStorage.setItem("pendingSigner", JSON.stringify(data));
      window.location.href = data.IDVerificationURL;
    }

  }, [signerDetails, fetching, hasRedirected]);
  useEffect(() => {
    if (signerDetails?.data) {
      const data = signerDetails.data;
      setState(prev => ({
        ...prev,
        isCancelled: data.IsCancelled,
        alreadySigned: data.IsSigned,
        signer: data,
        signerName: `${data.FirstName} ${data.LastName}`,
        termsAndConditions: (data.TermsAndConditions || []).map(item => ({
          ...item,
          IsClicked: false
        })),
        isValidSequenceSigner: data.IsValidSequenceSigner,
        IsValidateSigner: data.IsValidateSigner,
        IDVerificationURL: data.IDVerificationURL,
        sequenceSignerMessage: data.SequenceSignerMessage,
        portallogo: data.Settings?.ImageUrl,
        languageCode: data.Settings?.LanguageCode
      }));
    }
  }, [signerDetails]);

  useEffect(() => {
    if (createSignerUrl && typeof createSignerUrl === "string") {
      window.location.href = createSignerUrl;
    }
  }, [createSignerUrl]);

  useEffect(() => {
    if (state.languageCode === 1033) {
      i18n.changeLanguage('en');
    } else if (state.languageCode === 1044) {
      i18n.changeLanguage('no');
    }
  }, [state.languageCode, i18n]);


  const updateCheckbox = (checked) => {
    setState(prev => ({
      ...prev,
      agreedTnC: checked,
      AcceptedTimeStamp: convertUTCDateToLocalDate(new Date())
    }));
  };

  const onClickTermsOfUse = (index) => {
    const updated = state.termsAndConditions.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          IsClicked: true,
          ClickedTimeStamp: convertUTCDateToLocalDate(new Date())
        };
      }
      return item;
    });

    const allRead = updated.every(item => item.IsClicked);

    setState(prev => ({
      ...prev,
      termsAndConditions: updated,
      signer: {
        ...prev.signer,
        TermsAndConditions: updated
      },
      isTncRead: allRead
    }));
  };

  const onAcceptTermsAndConditions = async () => {
    if (!state.agreedTnC || !state.isTncRead) return;

    try {
      const response = dispatch(getIpAddress());
      const ipAddress = response?.ip || response?.data?.ip || "";

      const info = `System: ${getSystemInfo()}\nBrowser: ${getBrowserInfo()}\nIP: ${ipAddress}`;

      const terms = state.signer.TermsAndConditions.map(item => ({
        ...item,
        ClickedSessionAndDeviceInfo: info,
        AcceptedSessionAndDeviceInfo: info,
        IsAccepted: true,
        AcceptedTimeStamp: state.AcceptedTimeStamp
      }));

      const payload = {
        ...state.signer,
        TermsAndConditions: terms
      };

      if (!state.signer.IsValidateSigner && state.signer.SignerId &&
        !state.signer.IsSigned &&
        !state.signer.IsCancelled &&
        state.signer.IsValidSequenceSigner) {

        dispatch(createSigner(payload));
      }
      else if (state.signer.IsValidateSigner && state.signer.IDVerificationURL) {
        localStorage.setItem("pendingSigner", JSON.stringify(payload));
        window.location.href = state.signer.IDVerificationURL;
      }

    } catch (err) {
      console.error(err);
    }
  };

  if (fetching && !error) return <Loading />;
  if (error || state.genericError) return <ErrorPage />;

  return (
    <div>
      <div className="navbar navbar-inverse navbar-fixed-top">
        <div className="container justify-content-center">
          {state.portallogo ? (
            <img src={state.portallogo} height="50" alt="logo" />
          ) : (
            <span>E-Sign Portal</span>
          )}
        </div>
      </div>

      {!state.alreadySigned && !state.isCancelled && state.isValidSequenceSigner ? (
        <div className="col-md-6 col-md-push-3 inner-content-box">

          {window.globalConfig.UseTermsAndConditionPage === "true" ? (
            <div className="tnc-container">
              <h2 className="tnc-title">{state.signerName}</h2>

              <h3 className="tnc-subtitle">{t('esign.termsOfUseMsg')}</h3>

              <div className="tnc-list">
                {state.termsAndConditions.map((item, i) => (
                  <div key={i} className="tnc-item">

                    <input
                      type="checkbox"
                      checked={item.IsClicked}
                      readOnly
                    />

                    {item.TypeId === TYPE_URL && (
                      <a
                        href={item.URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => onClickTermsOfUse(i)}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        <span>{item.URL}</span>
                      </a>
                    )}

                    {item.TypeId === TYPE_DOCUMENT &&
                      item.Notes.map((note, j) => (
                        <a
                          key={j}
                          href={`data:${note.FileType};base64,${note.Base64String}`}
                          download={note.FileName}
                          onClick={() => onClickTermsOfUse(i)}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>{note.FileName}</span>
                        </a>
                      ))}
                  </div>
                ))}
              </div>

              <div className="tnc-agreement">
                <input
                  type="checkbox"
                  disabled={!state.isTncRead}
                  onChange={(e) => updateCheckbox(e.target.checked)}
                />
                <label>{t('esign.agreementMsg')}</label>
              </div>

              <button
                className={`tnc-button ${!state.agreedTnC ? 'disabled' : ''}`}
                onClick={onAcceptTermsAndConditions}
                disabled={!state.agreedTnC}
              >
                {t('esign.continueMsg')}
              </button>
            </div>
          ) : (
            <div className="centerTxt">
              <h4>{t('esign.redirectMsg')}</h4>
              <img src={BankIDlogoImage} height="40" alt="BankID" />
            </div>
          )}
        </div>
      ) : state.isCancelled ? (
        <p style={{ marginLeft: '10px', marginTop: '5%', textAlign: "center", fontSize: '25px', color: "Red" }}>{t('esign.isCancelledMsg')}</p>
      ) : state.alreadySigned ? (
        <p style={{ marginLeft: '10px', marginTop: '5%', textAlign: "center", fontSize: '25px', color: "Red" }}>{t('esign.alreadySignedMsg')}</p>
      ) : !state.isValidSequenceSigner ? (
        <p style={{ marginLeft: '10px', marginTop: '5%', textAlign: "center", fontSize: '25px', color: "Red" }}>{state.sequenceSignerMessage}</p>
      ) : null}
    </div>
  );
};

export default SignerDetails;